/* global Image */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import posed, {Transition} from 'react-pose';
import {navigate} from '@reach/router'; // eslint-disable-line
import qs from 'querystring';

import base64 from '../libs/b64';

import objectives from '../libs/objectives';
import security from '../libs/security';

import Step from '../components/Step';
import Results from '../components/Results';
import Thinking from '../components/Thinking';

import styles from './questions.module.css';

const questions = {objectives, security};

const easeIn = [0.4, 0.0, 1, 1];
const easeOut = [0.0, 0.0, 0.2, 1];

let nextPos = 0;

const Background = posed.div({
    preEnter: {
        opacity: 0,
    },
    enter: {
        opacity: 1,
        transition: {
            duration: 400,
            ease: easeOut,
        },
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 500,
            ease: easeIn,
        },
    },
});

class QuestionsPage extends Component {
    static propTypes = {
        hostRef: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            parsed: false,
            stepIndex: 0,
            steps: [objectives.start],
            responses: {},
            showResults: false,
            products: null,
        };
    }

    getStepName = () => {
        const {stepIndex, steps} = this.state;

        return steps[stepIndex].name;
    }

    getCurrentStep = () => {
        const {stepIndex, steps} = this.state;

        return steps[stepIndex];
    }

    static getDerivedStateFromProps(props, state) { // eslint-disable-line
        const {location} = props;
        const {stepIndex} = state;
        const query = qs.parse(location.search.replace(/^\?/, ''));

        if(typeof query !== 'undefined' && query.s) {
            const {si, r, s} = JSON.parse(base64.decode(query.s) || '{}');
            if(stepIndex > 0 && stepIndex === si)
                return null;

            const newState = {
                parsed: true,
                stepIndex: si,
                responses: r,
                steps: s.map(([group, name]) => questions[group][name]), // eslint-disable-line
                showResults: false,
            };

            if(newState.stepIndex >= newState.steps.length) {
                const products = [];
                Object.keys(questions).forEach((name) => {
                    const instance = questions[name];

                    if(!instance.recommendations)
                        return;

                    instance.recommendations.forEach((rec) => {
                        if(!rec.validate(newState.responses))
                            return;

                        const amount = rec.quantity(newState.responses);

                        if(amount > -1) {
                            const pos = products.findIndex(
                                (existingRec) => existingRec.asin === rec.asin,
                            );

                            if(pos > -1)
                                products[pos].amount += amount;
                            else
                                products.push({...rec, amount: Math.max(amount, 1)});
                        }
                    });
                });

                newState.products = products;
            }

            return newState;
        }

        return {
            parsed: true,
            stepIndex: 0,
            steps: [objectives.start],
            responses: {},
            showResults: false,
            products: null,
        };
    }

    next = async (selection) => {
        const {stepIndex, steps, responses} = this.state;
        const step = this.getCurrentStep();

        const updatedSteps = [...steps];
        const formattedResult = step.onSelect(selection, updatedSteps);

        let updatedResponses;
        if(!responses[step.group])
            updatedResponses = {
                ...responses,
                [step.group]: {
                    [this.getStepName()]: formattedResult,
                },
            };
        else
            updatedResponses = {
                ...responses,
                [step.group]: {
                    ...responses[step.group],
                    [this.getStepName()]: formattedResult,
                },
            };

        if(steps[stepIndex + 1] && steps[stepIndex + 1].image) {
            // preload next step image
            const preload = new Image();
            preload.src = steps[stepIndex + 1].image;
        }

        const encodableState = {
            si: stepIndex + 1,
            r: updatedResponses,
            s: updatedSteps.map((s) => [s.group, s.name]),
        };
        const stateStr = await base64.encode(JSON.stringify(encodableState));

        navigate(`/questions?s=${encodeURIComponent(stateStr)}`);
    }

    back = async () => {
        const {stepIndex, steps, responses} = this.state;
        const step = this.getCurrentStep();

        const updatedSteps = [...steps];
        const updatedResponses = {...responses};

        let selection;
        if(updatedResponses[step.group]) {
            const name = this.getStepName();
            selection = updatedResponses[step.group][name];
            delete updatedResponses[step.group][name];

            if(!Object.keys(updatedResponses[step.group]).length)
                delete updatedResponses[step.group];
        }

        if(step.onBack)
            step.onBack(selection, updatedSteps);

        const encodableState = {
            si: stepIndex - 1,
            r: updatedResponses,
            s: updatedSteps.map((s) => [s.group, s.name]),
        };

        const stateStr = await base64.encode(JSON.stringify(encodableState));

        navigate(`/questions?s=${encodeURIComponent(stateStr)}`);
    }

    productQuantityChange = (asin, newQuantity) => {
        if(newQuantity < 1 || newQuantity > 100)
            return;

        const {products} = this.state;
        const index = products.findIndex((product) => product.asin === asin);

        const newProducts = [...products];
        newProducts.splice(index, 1, {...products[index], amount: newQuantity});

        this.setState({products: newProducts});
    }

    render() {
        const {
            parsed, responses, stepIndex, showResults,
            products,
        } = this.state;
        const {hostRef} = this.props;
        const step = this.getCurrentStep();

        let element = null;

        if(!parsed)
            element = (<div key="empty" />);
        else if(step) {
            const {
                question, description, image,
                type, name, group, optionsText,
            } = step;
            let options;
            let initResult;
            if(step.options) {
                options = step.options(responses);
                if(responses[group] && responses[group][name])
                    initResult = responses[group][name] instanceof Array
                        ? responses[group][name].map(
                            (value) => options.find((opt) => opt.value === value),
                        )
                        : options.find((opt) => opt.value === responses[group][name]);
            } else if(responses[group] && responses[group][name])
                initResult = responses[group][name];

            element = (
                <Step
                    index={stepIndex}
                    key={`${group}${name}`}
                    type={type}
                    question={question}
                    description={description}
                    image={image}
                    optionsText={optionsText}
                    options={options}
                    initialResult={initResult}
                    onBack={this.back}
                    onComplete={this.next}
                    nextQuestionPos={nextPos}
                    onNextQuestionPos={(y, done) => {
                        nextPos = y;
                        done();
                    }}
                />
            );
        } else if(!showResults) { // eslint-disable-line
            element = (
                <Thinking
                    key="thinking"
                    onDone={() => {
                        this.setState({showResults: true});
                    }}
                />
            );
        } else if(products && showResults)
            element = (
                <Results
                    key="results"
                    products={products}
                    responses={responses}
                    questions={questions}
                    onQuantityChange={this.productQuantityChange}
                />
            );

        return (
            <div
                className={styles.steps}
                ref={hostRef}
            >
                <Transition preEnterPose="preEnter" nextStep>
                    {element}
                    {(products && showResults) && <Background key="background" className={styles.background} />}
                </Transition>
            </div>
        );
    }
}

export default posed(QuestionsPage)({
    preEnter: {
        y: 0,
        opacity: 0,
    },
    enter: {
        opacity: 1,
        transition: {
            duration: 0,
        },
        beforeChildren: true,
    },
    exit: {
        afterChildren: true,
    },
});
